import React, { useEffect, useMemo, useState } from 'react';
import {
  FaDownload,
  FaLink,
  FaPlug,
  FaPlus,
  FaRegEdit,
  FaSave,
  FaSync,
  FaTimes,
  FaToggleOff,
  FaToggleOn,
  FaTrash,
} from 'react-icons/fa';
import api from '../api';

const METHOD_OPTIONS = [
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
];

const EVENT_OPTIONS = [
  { value: 'PROCESS_COMPLETED', label: 'Quando o processo for concluído' },
  { value: 'STEP_COMPLETED', label: 'Quando uma etapa for concluída' },
];

const STATUS_EXPORT_OPTIONS = [
  { value: 'COMPLETED', label: 'Somente solicitações concluídas' },
  { value: 'ACTIVE', label: 'Somente solicitações em andamento' },
  { value: 'ALL', label: 'Todas as solicitações' },
];

const emptyHeaderEntry = () => ({ id: Math.random().toString(36).slice(2), key: '', value: '' });

const mapHeadersToEntries = (headers) => {
  if (!headers || typeof headers !== 'object') {
    return [emptyHeaderEntry()];
  }
  const entries = Object.entries(headers).map(([key, value]) => ({
    id: Math.random().toString(36).slice(2),
    key,
    value: value !== undefined && value !== null ? String(value) : '',
  }));
  return entries.length ? entries : [emptyHeaderEntry()];
};

const createEmptyForm = (template) => {
  const base = {
    name: '',
    targetUrl: '',
    method: 'POST',
    events: ['PROCESS_COMPLETED'],
    active: true,
    timeoutMs: '10000',
    headers: [emptyHeaderEntry()],
  };

  if (!template || typeof template !== 'object') {
    return base;
  }

  const methodValues = METHOD_OPTIONS.map((option) => option.value);
  const validMethod =
    typeof template.method === 'string' && methodValues.includes(template.method)
      ? template.method
      : base.method;

  const allowedEvents = EVENT_OPTIONS.map((option) => option.value);
  const templateEvents = Array.isArray(template.events)
    ? template.events.filter((event) => allowedEvents.includes(event))
    : [];

  const timeoutValue = Number(template.timeoutMs);

  return {
    ...base,
    name: typeof template.name === 'string' ? template.name : base.name,
    targetUrl: typeof template.targetUrl === 'string' ? template.targetUrl : base.targetUrl,
    method: validMethod,
    events: templateEvents.length ? templateEvents : base.events,
    active:
      template.active !== undefined && template.active !== null
        ? Boolean(template.active)
        : base.active,
    timeoutMs:
      Number.isFinite(timeoutValue) && timeoutValue > 0
        ? String(Math.floor(timeoutValue))
        : base.timeoutMs,
    headers: template.headers ? mapHeadersToEntries(template.headers) : base.headers,
  };
};

const buildHeadersPayload = (entries) => {
  const normalized = {};
  entries
    .filter((entry) => entry && entry.key && entry.key.trim())
    .forEach((entry) => {
      const key = entry.key.trim();
      const value = entry.value !== undefined && entry.value !== null ? String(entry.value) : '';
      normalized[key] = value;
    });
  return Object.keys(normalized).length ? normalized : null;
};

const safeProcessName = (name, processId) => {
  if (!name) return `processo-${processId}`;
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
};

export default function ProcessIntegrationSettings({ processId, processName, template }) {
  const initialTemplate = useMemo(() => {
    if (!template || typeof template !== 'object') {
      return null;
    }
    return {
      ...template,
    };
  }, [template]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(Boolean(initialTemplate));
  const [formData, setFormData] = useState(() => createEmptyForm(initialTemplate));
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportStatus, setExportStatus] = useState('COMPLETED');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const processLabel = useMemo(() => {
    return processName ? `"${processName}"` : `#${processId}`;
  }, [processId, processName]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/processes/${processId}/integrations`);
        if (!cancelled) {
          setIntegrations(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err?.response?.data?.error || 'Erro ao carregar integrações';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [processId]);

  useEffect(() => {
    if (!initialTemplate) {
      return;
    }
    setEditingIntegration(null);
    setFormError('');
    setFormData(createEmptyForm(initialTemplate));
    setFormOpen(true);
  }, [initialTemplate]);

  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(''), 5000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const startCreating = () => {
    setFormData(createEmptyForm());
    setEditingIntegration(null);
    setFormOpen(true);
    setFormError('');
  };

  const startEditing = (integration) => {
    setEditingIntegration(integration);
    setFormOpen(true);
    setFormError('');
    setFormData({
      name: integration.name || '',
      targetUrl: integration.targetUrl || '',
      method: integration.method || 'POST',
      events: Array.isArray(integration.events) && integration.events.length
        ? integration.events
        : ['PROCESS_COMPLETED'],
      active: Boolean(integration.active),
      timeoutMs: integration.timeoutMs ? String(integration.timeoutMs) : '10000',
      headers: mapHeadersToEntries(integration.headers),
    });
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormError('');
    setEditingIntegration(null);
    setFormData(createEmptyForm(initialTemplate));
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleEvent = (eventName) => {
    setFormData((prev) => {
      const exists = prev.events.includes(eventName);
      const events = exists
        ? prev.events.filter((event) => event !== eventName)
        : [...prev.events, eventName];
      return { ...prev, events };
    });
  };

  const updateHeaderEntry = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry,
      ),
    }));
  };

  const addHeaderEntry = () => {
    setFormData((prev) => ({ ...prev, headers: [...prev.headers, emptyHeaderEntry()] }));
  };

  const removeHeaderEntry = (id) => {
    setFormData((prev) => {
      const filtered = prev.headers.filter((entry) => entry.id !== id);
      return { ...prev, headers: filtered.length ? filtered : [emptyHeaderEntry()] };
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (saving) return;
    setFormError('');

    const trimmedName = formData.name.trim();
    const trimmedUrl = formData.targetUrl.trim();

    if (!trimmedName) {
      setFormError('Informe um nome para a integração');
      return;
    }

    if (!trimmedUrl) {
      setFormError('Informe a URL da integração');
      return;
    }

    const selectedEvents = formData.events.filter(Boolean);
    if (!selectedEvents.length) {
      setFormError('Selecione ao menos um evento para disparar a integração');
      return;
    }

    const timeoutValue = Number(formData.timeoutMs);
    if (!Number.isFinite(timeoutValue) || timeoutValue <= 0) {
      setFormError('Informe um tempo máximo de resposta válido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: trimmedName,
        targetUrl: trimmedUrl,
        method: formData.method || 'POST',
        events: selectedEvents,
        active: Boolean(formData.active),
        timeoutMs: timeoutValue,
        headers: buildHeadersPayload(formData.headers),
      };

      if (editingIntegration) {
        await api.put(`/processes/integrations/${editingIntegration.id}`, payload);
        setFeedback('Integração atualizada com sucesso.');
      } else {
        await api.post(`/processes/${processId}/integrations`, payload);
        setFeedback('Integração criada com sucesso.');
      }

      const { data } = await api.get(`/processes/${processId}/integrations`);
      setIntegrations(Array.isArray(data) ? data : []);
      closeForm();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao salvar integração';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (integration) => {
    try {
      await api.put(`/processes/integrations/${integration.id}`, {
        name: integration.name,
        targetUrl: integration.targetUrl,
        method: integration.method,
        events: integration.events,
        headers: integration.headers,
        timeoutMs: integration.timeoutMs,
        active: !integration.active,
      });
      setFeedback(`Integração ${integration.active ? 'desativada' : 'ativada'} com sucesso.`);
      const { data } = await api.get(`/processes/${processId}/integrations`);
      setIntegrations(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao atualizar integração';
      setFeedback(message);
    }
  };

  const deleteIntegration = async (integration) => {
    if (!window.confirm(`Remover a integração "${integration.name}"?`)) return;
    try {
      await api.delete(`/processes/integrations/${integration.id}`);
      setFeedback('Integração removida com sucesso.');
      const { data } = await api.get(`/processes/${processId}/integrations`);
      setIntegrations(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao remover integração';
      setFeedback(message);
    }
  };

  const exportData = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = {
        status: exportStatus,
      };
      if (exportStartDate) {
        params.startDate = exportStartDate;
      }
      if (exportEndDate) {
        params.endDate = exportEndDate;
      }
      const { data } = await api.get(`/processes/${processId}/integrations/export`, { params });
      const filenameBase = safeProcessName(processName, processId);
      const filename = `${filenameBase || 'processo'}-solicitacoes.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setFeedback('Arquivo de exportação gerado com sucesso.');
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao exportar dados';
      setExportError(message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-sky-500 text-white shadow-inner">
            <FaPlug />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Integrações do processo {processLabel}</h1>
            <p className="mt-1 text-sm text-slate-600">
              Configure integrações automáticas e exporte dados filtrados das solicitações para conectar este processo a outros sistemas.
            </p>
          </div>
        </div>
      </div>

      {feedback && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 shadow-sm">
          {feedback}
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      )}

      <div className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Integrações configuradas</h2>
            <p className="text-sm text-slate-600">
              Cadastre URLs para receber os dados das solicitações quando eventos selecionados ocorrerem.
            </p>
          </div>
          <button
            type="button"
            onClick={startCreating}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:from-blue-500 hover:to-sky-400"
          >
            <FaPlus /> Nova integração
          </button>
        </div>

        {formOpen && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-base font-semibold text-blue-900">
                {editingIntegration ? `Editar integração "${editingIntegration.name}"` : 'Nova integração'}
              </h3>
              <button
                type="button"
                onClick={closeForm}
                className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-600 shadow-sm transition hover:bg-blue-50"
              >
                <FaTimes /> Cancelar
              </button>
            </div>
            {formError && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}
            <form className="mt-4 space-y-4" onSubmit={submitForm}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Nome</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(event) => updateFormField('name', event.target.value)}
                    placeholder="Ex.: Disparo para CRM"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">URL de destino</label>
                  <input
                    type="url"
                    value={formData.targetUrl}
                    onChange={(event) => updateFormField('targetUrl', event.target.value)}
                    placeholder="https://api.exemplo.com/webhook"
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Método HTTP</label>
                  <select
                    value={formData.method}
                    onChange={(event) => updateFormField('method', event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    {METHOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Tempo máximo (ms)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.timeoutMs}
                    onChange={(event) => updateFormField('timeoutMs', event.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <button
                    type="button"
                    onClick={() => updateFormField('active', !formData.active)}
                    className={`mt-1 inline-flex w-full items-center justify-between rounded-xl border px-3 py-2 text-sm font-medium transition ${
                      formData.active
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-50 text-slate-500'
                    }`}
                  >
                    {formData.active ? (
                      <>
                        <span>Ativa</span>
                        <FaToggleOn />
                      </>
                    ) : (
                      <>
                        <span>Desativada</span>
                        <FaToggleOff />
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <span className="block text-sm font-medium text-slate-700">Eventos monitorados</span>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  {EVENT_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.events.includes(option.value)}
                        onChange={() => toggleEvent(option.value)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Cabeçalhos HTTP (opcional)</label>
                <div className="mt-2 space-y-2">
                  {formData.headers.map((entry) => (
                    <div key={entry.id} className="grid gap-2 md:grid-cols-[1fr,1fr,auto]">
                      <input
                        type="text"
                        value={entry.key}
                        placeholder="Nome do header"
                        onChange={(event) => updateHeaderEntry(entry.id, 'key', event.target.value)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                      <input
                        type="text"
                        value={entry.value}
                        placeholder="Valor"
                        onChange={(event) => updateHeaderEntry(entry.id, 'value', event.target.value)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                      <button
                        type="button"
                        onClick={() => removeHeaderEntry(entry.id)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-100"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addHeaderEntry}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600"
                  >
                    <FaPlus /> Adicionar header
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <FaSync className="animate-spin" /> Salvando...
                    </>
                  ) : (
                    <>
                      <FaSave /> Salvar integração
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="py-6 text-center text-sm text-slate-500">Carregando integrações...</div>
        ) : integrations.length ? (
          <div className="grid gap-4 md:grid-cols-2">
            {integrations.map((integration) => {
              const eventsSummary = Array.isArray(integration.events)
                ? integration.events
                    .map((event) => EVENT_OPTIONS.find((option) => option.value === event)?.label || event)
                    .join(', ')
                : 'Nenhum evento configurado';
              return (
                <div
                  key={integration.id}
                  className="flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{integration.name}</h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                            <FaLink />
                            {integration.method}
                          </span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                            integration.active
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-200 text-slate-500'
                          }`}
                          >
                            {integration.active ? 'Ativa' : 'Desativada'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      {integration.targetUrl}
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Eventos</span>
                      <p className="mt-1 text-sm text-slate-600">{eventsSummary}</p>
                    </div>
                    {integration.headers && Object.keys(integration.headers).length > 0 ? (
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Headers</span>
                        <ul className="mt-1 space-y-1 text-sm text-slate-600">
                          {Object.entries(integration.headers).map(([key, value]) => (
                            <li key={key} className="flex items-center justify-between gap-2 rounded-lg bg-slate-100 px-3 py-1">
                              <span className="font-medium">{key}</span>
                              <span className="truncate text-xs text-slate-500">{String(value)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Nenhum header personalizado.</p>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => toggleActive(integration)}
                      className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100"
                    >
                      {integration.active ? <FaToggleOff /> : <FaToggleOn />}
                      {integration.active ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => startEditing(integration)}
                      className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-100"
                    >
                      <FaRegEdit /> Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteIntegration(integration)}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-100"
                    >
                      <FaTrash /> Remover
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center text-sm text-slate-500">
            Nenhuma integração configurada até o momento.
          </div>
        )}
      </div>

      <div className="space-y-4 rounded-3xl border border-slate-200/70 bg-white/95 p-6 shadow-xl">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Exportação de dados das solicitações</h2>
          <p className="text-sm text-slate-600">
            Gere um arquivo JSON com os resultados das solicitações deste processo, filtrando por período e status.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Data inicial</label>
            <input
              type="date"
              value={exportStartDate}
              onChange={(event) => setExportStartDate(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Data final</label>
            <input
              type="date"
              value={exportEndDate}
              onChange={(event) => setExportEndDate(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Status</label>
            <select
              value={exportStatus}
              onChange={(event) => setExportStatus(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {STATUS_EXPORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {exportError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {exportError}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={exportData}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-md transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? (
              <>
                <FaSync className="animate-spin" /> Gerando...
              </>
            ) : (
              <>
                <FaDownload /> Baixar JSON
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
